
import React, { useState, useEffect, useRef } from 'react';
import { StorageService } from '../services/storageService';
import { CandidateAttempt, Question, AttemptStatus, Assessment } from '../types';

interface AssessmentSessionProps {
  attemptId: string;
  onFinish: () => void;
  existingStream?: MediaStream | null;
}

const AssessmentSession: React.FC<AssessmentSessionProps> = ({ attemptId, onFinish, existingStream }) => {
  const [attempt, setAttempt] = useState<CandidateAttempt | null>(null);
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isLocked, setIsLocked] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (indicatorRef.current) {
      const activeElement = indicatorRef.current.children[currentIdx] as HTMLElement;
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [currentIdx]);

  useEffect(() => {
    loadSession();
  }, [attemptId]);

  const loadSession = async () => {
    const att = await StorageService.getAttempt(attemptId);
    if (!att) return;

    const assessments = await StorageService.getAssessments();
    const ass = assessments.find(a => a.id === att.assessmentId);
    if (!ass) return;

    const allQs = await StorageService.getQuestions(att.assessmentId);
    const orderedQs = att.questionOrder.map(id => allQs.find(q => q.id === id)).filter(Boolean) as Question[];

    setAttempt(att);
    setAssessment(ass);
    setQuestions(orderedQs);

    // Initialize Camera if required
    if (ass.requireCamera) {
      if (existingStream) {
        setCameraStream(existingStream);
      } else {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          setCameraStream(stream);
        } catch (err) {
          setCameraError('Camera access denied or not found. Please allow camera access to continue.');
        }
      }
    }

    const elapsedSecs = Math.floor((Date.now() - att.startTime) / 1000);
    const totalSecs = ass.duration * 60;
    const remaining = Math.max(0, totalSecs - elapsedSecs);
    setTimeLeft(remaining);

    if (att.status === AttemptStatus.LOCKED) {
      setIsLocked(true);
    }
  };

  useEffect(() => {
    if (timeLeft > 0 && !isLocked && !isDisqualified && attempt?.status === AttemptStatus.IN_PROGRESS) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [timeLeft, isLocked, isDisqualified, attempt?.status]);

  useEffect(() => {
    if (videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream, attempt]); // Re-attach if attempt loads/changes

  // Camera Snapshot Loop for Admin Monitoring
  useEffect(() => {
    if (!assessment?.requireCamera || !cameraStream || !attempt || attempt.status !== AttemptStatus.IN_PROGRESS) {
      return;
    }

    console.log("📸 Snapshot monitoring active for attempt:", attempt.id);

    const captureAndUpload = async () => {
      if (!videoRef.current) {
        console.log("⚠️ Video ref not available");
        return;
      }

      // Wait for video to have enough data
      if (videoRef.current.readyState < 3 || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
        console.log("⏳ Video not ready - readyState:", videoRef.current.readyState, "dimensions:", videoRef.current.videoWidth, "x", videoRef.current.videoHeight);
        return;
      }

      try {
        const canvas = document.createElement('canvas');
        canvas.width = 320;
        canvas.height = 240;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);

          // Skip if image appears to be black
          if (dataUrl.length < 8000) {
            console.log("⚠️ Image too small (black screen), skipping");
            return;
          }

          console.log("✅ Snapshot captured:", dataUrl.length, "bytes");

          await StorageService.updateAttempt({
            ...attempt,
            id: attempt.id,
            lastSnapshot: dataUrl,
            lastSnapshotTime: Date.now()
          } as CandidateAttempt);

          console.log("✅ Uploaded to Firestore");
        }
      } catch (err) {
        console.error("❌ Snapshot capture failed:", err);
      }
    };

    let intervalId: ReturnType<typeof setInterval> | null = null;

    // Wait 5 seconds before starting to ensure video is fully loaded
    const startDelay = setTimeout(() => {
      captureAndUpload();
      intervalId = setInterval(captureAndUpload, 15000); // Every 15 seconds
    }, 5000);

    return () => {
      clearTimeout(startDelay);
      if (intervalId) clearInterval(intervalId);
    };
  }, [assessment, cameraStream, attempt?.status]);

  // Connect camera stream to video element
  useEffect(() => {
    if (videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream]);


  useEffect(() => {
    const handleBlur = () => {
      if (!isLocked && !isDisqualified && attempt?.status === AttemptStatus.IN_PROGRESS) {
        handleLock('Security Violation: Window/Tab switch detected');
      }
    };

    window.addEventListener('blur', handleBlur);
    return () => window.removeEventListener('blur', handleBlur);
  }, [isLocked, isDisqualified, attempt]);

  const handleLock = async (reason: string) => {
    if (!attempt) return;

    const nextViolations = attempt.violations + 1;
    const limit = assessment?.maxViolations || 3;

    if (nextViolations >= limit) {
      setIsDisqualified(true);
      const updated: CandidateAttempt = {
        ...attempt,
        status: AttemptStatus.ABANDONED,
        violations: nextViolations,
        auditLogs: [...attempt.auditLogs, {
          id: 'l_auto_' + Date.now(),
          timestamp: Date.now(),
          type: 'AUTO_SUBMIT',
          details: 'Disqualified: Violation threshold reached'
        }]
      };
      await StorageService.updateAttempt(updated);
    } else {
      setIsLocked(true);
      const updated: CandidateAttempt = {
        ...attempt,
        status: AttemptStatus.LOCKED,
        violations: nextViolations,
        auditLogs: [...attempt.auditLogs, {
          id: 'l_' + Date.now(),
          timestamp: Date.now(),
          type: 'TAB_SWITCH',
          details: reason
        }]
      };
      setAttempt(updated);
      await StorageService.updateAttempt(updated);
    }
  };

  const handleResume = async () => {
    if (pinInput === assessment?.resumePin) {
      setIsLocked(false);
      setPinInput('');
      setPinError('');
      const updated = { ...attempt!, status: AttemptStatus.IN_PROGRESS };
      setAttempt(updated);
      await StorageService.updateAttempt(updated);
    } else {
      setPinError('Invalid Proctor Resume PIN');
    }
  };

  const handleAnswer = async (qId: string, option: string) => {
    if (!attempt) return;
    const updated = {
      ...attempt,
      responses: { ...attempt.responses, [qId]: option }
    };
    setAttempt(updated);
    await StorageService.updateAttempt(updated);
  };

  const handleSubmit = async () => {
    if (!attempt || !questions || isSubmitting) return;
    setIsSubmitting(true);

    let score = 0;
    questions.forEach(q => {
      if (attempt.responses[q.id] === q.correctOption) {
        score += q.marks;
      }
    });

    const updated: CandidateAttempt = {
      ...attempt,
      status: AttemptStatus.SUBMITTED,
      endTime: Date.now(),
      score,
      auditLogs: [...attempt.auditLogs, {
        id: 'l_sub_' + Date.now(),
        timestamp: Date.now(),
        type: 'SUBMIT',
        details: 'Assessment submitted'
      }]
    };
    await StorageService.updateAttempt(updated);
    onFinish();
  };

  if (!attempt || !assessment) return (
    <div className="p-20 flex flex-col items-center justify-center">
      <div className="loader"></div>
      <p className="mt-4 text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest transition-colors">Entering Secure Session...</p>
    </div>

  );

  // Blocking Camera Error
  if (assessment.requireCamera && cameraError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-red-50 dark:bg-red-900/10">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-3xl p-10 text-center shadow-xl border-2 border-red-100 dark:border-red-900/50">
          <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">📷</div>
          <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-4">Camera Required</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">{cameraError}</p>
          <button onClick={() => window.location.reload()} className="bg-indigo-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-indigo-700 transition">Try Again</button>
        </div>
      </div>
    );
  }

  // Waiting for Camera
  if (assessment.requireCamera && !cameraStream) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-4">
        <div className="loader"></div>
        <p className="font-bold text-gray-500 uppercase tracking-widest text-xs">Requesting Camera Access...</p>
      </div>
    );
  }

  if (isDisqualified) {
    return (
      <div className="fixed inset-0 bg-red-600 z-[200] flex items-center justify-center p-8 text-white text-center">
        <button
          onClick={() => {
            StorageService.logout();
            window.location.href = '/';
          }}
          className="absolute top-8 right-8 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-2xl font-bold transition-all hover:scale-110 active:scale-95"
          title="Close and Return Home"
        >
          ✕
        </button>
        <div className="max-w-xl space-y-8">
          <div className="w-24 h-24 bg-white/20 rounded-3xl flex items-center justify-center mx-auto text-4xl mb-8">⚠️</div>
          <h1 className="text-3xl font-black mb-6">Session Flagged</h1>
          <p className="text-xl font-medium leading-relaxed">
            Unusual activity was detected that does not comply with our interview integrity guidelines. As a result, this interview session has been flagged for review.
          </p>
        </div>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div className="fixed inset-0 bg-white/95 dark:bg-gray-950/95 backdrop-blur-md z-[100] flex items-center justify-center p-4 transition-colors">
        <div className="max-w-md w-full text-center space-y-8">
          <div className="w-24 h-24 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-3xl flex items-center justify-center mx-auto text-4xl transform rotate-3 shadow-xl">🔒</div>
          <div className="space-y-2">
            <h2 className="text-3xl font-black text-gray-900 dark:text-white transition-colors">Assessment Locked</h2>
          </div>

          <div className="space-y-4">
            <div className="relative w-full">
              <input
                type="text"
                name="pincode_entry_field"
                id="pincode_entry_field"
                autoComplete="off"
                className="peer absolute inset-0 w-full h-full opacity-0 z-10 cursor-text font-mono"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleResume()}
              />
              <div className="uppercase w-full px-4 py-4 border-2 border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 rounded-2xl text-center text-2xl font-black tracking-[1em] peer-focus:border-indigo-500 outline-none text-gray-900 dark:text-white transition-all flex items-center justify-center h-[72px]">
                {pinInput ? '•'.repeat(pinInput.length) : <span className="text-gray-400">••••••</span>}
              </div>
            </div>
            {pinError && <p className="text-red-600 dark:text-red-400 text-xs font-bold uppercase">{pinError}</p>}
            <button onClick={handleResume} className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold text-lg shadow-xl shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 transition-all">Resume Assessment</button>
          </div>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentIdx];
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-4xl mx-auto min-h-[calc(100vh-8rem)] flex flex-col justify-center px-4 py-8">
      {/* Header Info */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-900 border border-amber-200/50 dark:border-amber-500/30 rounded-xl p-2 md:px-4 md:py-2 flex items-center justify-between shadow-sm transition-colors mb-4">
        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-500">
          <span className="bg-amber-50 dark:bg-amber-900/40 w-6 h-6 rounded-full flex items-center justify-center text-sm">⚠️</span>
          <div>
            <p className="font-black uppercase text-[8px] tracking-widest opacity-80">Integrity Checks Active</p>
            <p className="text-[10px] font-bold">Do not switch tabs.</p>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-lg border-2 font-mono text-base font-black shadow-inner transition-colors ${timeLeft < 300 ? 'border-red-500 text-red-600 animate-pulse' : 'border-indigo-50 dark:border-gray-800 text-indigo-700 dark:text-indigo-400'}`}>
          {formatTime(timeLeft)}
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl md:rounded-[2rem] shadow-xl border border-gray-100 dark:border-gray-800 p-6 md:p-10 transition-colors flex flex-col relative">
        {/* Progress & Header */}
        <div className="flex-shrink-0 flex items-center justify-between mb-6">
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-widest">Question {currentIdx + 1} of {questions.length}</span>
          </div>
          <div ref={indicatorRef} className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2 max-w-[150px] sm:max-w-[250px] md:max-w-[400px] lg:max-w-[500px]">
            {questions.map((_, i) => (
              <div
                key={i}
                className={`flex-shrink-0 h-1.5 rounded-full transition-all duration-500 ${i === currentIdx ? 'bg-indigo-600 w-8' :
                  attempt.responses[questions[i].id] ? 'bg-indigo-100 dark:bg-indigo-900/60 w-3' : 'bg-gray-50 dark:bg-gray-800 w-3'
                  }`}
              />
            ))}
          </div>
        </div>


        {/* content */}
        <div className="flex flex-col">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-8 leading-snug transition-colors">
            {currentQ?.text}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-8">
            {(['A', 'B', 'C', 'D'] as const).map((opt) => (
              <button
                key={opt}
                onClick={() => handleAnswer(currentQ.id, opt)}
                className={`p-3 md:p-3.5 rounded-xl md:rounded-2xl border-2 text-left transition-all flex items-center gap-3 group hover:scale-[1.01] active:scale-[0.98] ${attempt.responses[currentQ.id] === opt
                  ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-900 dark:text-white shadow-lg ring-4 ring-indigo-500/10'
                  : 'border-gray-100 dark:border-gray-800 hover:border-indigo-200 dark:hover:border-indigo-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
              >
                <div className={`flex-shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-lg md:rounded-xl flex items-center justify-center font-black text-xs md:text-sm border-2 transition-all ${attempt.responses[currentQ.id] === opt
                  ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg rotate-3'
                  : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 text-gray-400 group-hover:border-indigo-200 group-hover:text-indigo-500'
                  }`}>
                  {opt}
                </div>
                <span className="text-sm md:text-base font-bold transition-colors">{(currentQ.options as any)[opt]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex-shrink-0 flex items-center justify-between pt-3 mt-auto border-t border-gray-100 dark:border-gray-800">
          <button disabled={currentIdx === 0} onClick={() => setCurrentIdx(prev => prev - 1)} className="px-4 py-2 text-xs md:text-sm text-gray-400 dark:text-gray-300 font-bold hover:text-gray-900 dark:hover:text-white disabled:opacity-20 transition-colors">Previous</button>

          {currentIdx === questions.length - 1 ? (
            <button disabled={isSubmitting} onClick={() => setShowConfirmSubmit(true)} className="bg-green-600 text-white px-6 py-2 md:px-10 md:py-2.5 rounded-xl font-bold text-xs md:text-sm shadow-xl shadow-green-200 dark:shadow-none flex items-center gap-2 hover:bg-green-700 hover:scale-105 transition-all">
              {isSubmitting ? <div className="loader !w-4 !h-4 !border-t-white"></div> : 'Finish Test'}
            </button>
          ) : (
            <button onClick={() => setCurrentIdx(prev => prev + 1)} className="bg-indigo-600 text-white px-6 py-2 md:px-10 md:py-2.5 rounded-xl font-bold text-xs md:text-sm shadow-xl shadow-indigo-200 dark:shadow-none hover:bg-indigo-700 hover:scale-105 transition-all">Next Question</button>
          )}
        </div>
      </div>

      {
        showConfirmSubmit && (
          <div className="fixed inset-0 bg-gray-950/80 backdrop-blur-sm z-[150] flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-white dark:bg-gray-900 rounded-[2.5rem] p-10 max-w-md w-full text-center shadow-2xl transform animate-in zoom-in-95 duration-300 border border-gray-100 dark:border-gray-800">
              {/* <div className="w-20 h-20 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner transition-colors">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div> */}
              <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-2 tracking-tight transition-colors">Ready to Submit?</h3>
              <p className="text-gray-500 dark:text-gray-400 font-medium mb-8 transition-colors">
                Review once before finalizing your submission.
              </p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 group"
                >
                  {isSubmitting ? <div className="loader !w-5 !h-5 !border-t-white"></div> : (
                    <span>Submit Assessment</span>
                  )}
                </button>
                <button
                  onClick={() => setShowConfirmSubmit(false)}
                  className="w-full py-4 font-bold text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  Go Back & Review
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Hidden video element for snapshot capture - invisible but rendered with proper dimensions */}
      {
        cameraStream && (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            width={320}
            height={240}
            className="fixed top-0 left-0 pointer-events-none opacity-5"
            style={{ zIndex: -50 }} // Behind everything but nominally on screen
          />
        )
      }
    </div >
  );
};

export default AssessmentSession;
